import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Divider,
  Stack,
  Collapse,
  Button,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Timeline as TimelineIcon,
  Functions as FunctionIcon,
  Message as MessageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  Memory as TokenIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const TraceDrawer = ({ open, onClose, traceData }) => {
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  const toggleStepExpansion = (stepNumber) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepNumber)) {
      newExpanded.delete(stepNumber);
    } else {
      newExpanded.add(stepNumber);
    }
    setExpandedSteps(newExpanded);
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getStepIcon = (type) => {
    switch (type) {
      case 'function_call':
        return <FunctionIcon sx={{ color: '#4caf50' }} />;
      case 'assistant_message':
        return <MessageIcon sx={{ color: '#2196f3' }} />;
      default:
        return <TimelineIcon sx={{ color: '#757575' }} />;
    }
  };

  const getStepColor = (type) => {
    switch (type) {
      case 'function_call':
        return '#4caf50';
      case 'assistant_message':
        return '#2196f3';
      default:
        return '#757575';
    }
  };

  if (!traceData) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 480,
            p: 0,
          },
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum trace disponível
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Execute uma conversa com o agente para ver o trace
          </Typography>
        </Box>
      </Drawer>
    );
  }

  const trace = traceData || {};  // 🆕 Garantir que trace não seja null/undefined

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 600, md: 700 },
          p: 0,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="600">
            Agent Workflow
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Trace Info Header */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Trace ID: {trace?.trace_id || 'N/A'}
        </Typography>
        
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            icon={<TimeIcon />}
            label={formatDuration(trace?.total_duration_ms || 0)}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<TokenIcon />}
            label={`${trace?.summary?.total_tokens || 0} tokens`}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<AnalyticsIcon />}
            label={`${trace?.summary?.total_steps || 0} steps`}
            size="small"
            variant="outlined"
          />
        </Stack>

        {trace?.summary?.tools_used?.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Tools utilizadas:
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {(trace?.summary?.tools_used || []).map((tool, index) => (
                <Chip
                  key={index}
                  label={tool}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Custom Timeline */}
      <Box sx={{ flex: 1, overflow: 'auto', pb: 2 }}>
        <Stack spacing={0} sx={{ px: 2 }}>
          {(trace?.steps || []).map((step, index) => (
            <Box key={step.step_number} sx={{ display: 'flex', minHeight: 'auto' }}>
              {/* Timeline Left Side */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mr: 2,
                  minWidth: 40,
                }}
              >
                {/* Step Icon */}
                <Avatar
                  sx={{
                    bgcolor: getStepColor(step.type),
                    width: 32,
                    height: 32,
                    mb: 1,
                  }}
                >
                  {getStepIcon(step.type)}
                </Avatar>
                
                {/* Connector Line */}
                {index < trace.steps.length - 1 && (
                  <Box
                    sx={{
                      width: 2,
                      minHeight: 60,
                      bgcolor: 'grey.300',
                      borderRadius: 1,
                    }}
                  />
                )}
              </Box>

              {/* Timeline Content */}
              <Box sx={{ flex: 1, pb: index < trace.steps.length - 1 ? 2 : 0 }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'grey.200',
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: getStepColor(step.type),
                        boxShadow: 1,
                      },
                    }}
                  >
                    {/* Step Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="600">
                        {step.type === 'function_call'
                          ? step.details.function_name
                          : 'Assistant Response'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={formatDuration(step.duration_ms)}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Button
                          size="small"
                          onClick={() => toggleStepExpansion(step.step_number)}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          {expandedSteps.has(step.step_number) ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </Button>
                      </Stack>
                    </Box>

                    {/* Quick Info */}
                    {step.type === 'function_call' && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {step.details.arguments?.query || 'Function call'}
                        </Typography>
                        
                        {/* Knowledge Base Info */}
                        {step.details.function_name?.includes('search_knowledge_base') && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            <Chip 
                              size="small" 
                              label={`🗂️ ${step.details.function_name.replace('search_knowledge_base_', '').replace('_specific', '').replace('_', ' ')}`}
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                            <Chip 
                              size="small" 
                              label="🔍 Busca Semântica"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                            {step.details.arguments?.threshold && (
                              <Chip 
                                size="small" 
                                label={`Parâmetro: ${step.details.arguments.threshold}`}
                                color="secondary"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 18 }}
                              />
                            )}
                            {step.details.search_results?.search_info?.threshold_used && 
                             step.details.search_results.search_info.threshold_used !== step.details.arguments?.threshold && (
                              <Chip 
                                size="small" 
                                label={`Usado: ${step.details.search_results.search_info.threshold_used} (KB)`}
                                color="warning"
                                variant="filled"
                                sx={{ fontSize: '0.65rem', height: 18 }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {step.type === 'assistant_message' && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {step.details.content_length} caracteres
                        </Typography>
                      </Box>
                    )}

                    {/* Token Usage */}
                    {step.details.usage && (
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          label={`${step.details.usage.input_tokens} in`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 18 }}
                        />
                        <Chip
                          label={`${step.details.usage.output_tokens} out`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 18 }}
                        />
                      </Stack>
                    )}

                    {/* Expanded Details */}
                    <Collapse in={expandedSteps.has(step.step_number)}>
                      <Divider sx={{ my: 1 }} />
                      
                      {step.type === 'function_call' && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Argumentos:
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1,
                              mt: 0.5,
                              bgcolor: 'grey.50',
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              overflow: 'auto',
                              maxHeight: 200,
                            }}
                          >
                            <pre style={{ 
                              whiteSpace: 'pre-wrap', 
                              wordWrap: 'break-word', 
                              overflowWrap: 'break-word',
                              margin: 0
                            }}>
                              {JSON.stringify(step.details.arguments, null, 2)}
                            </pre>
                          </Paper>
                          
                          {/* Search Results for Knowledge Base */}
                          {step.details.function_name?.includes('search_knowledge_base') && step.details.search_results && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Resultados da Busca:
                              </Typography>
                              <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                                {step.details.search_results.search_info && (
                                  <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    <Chip 
                                      size="small" 
                                      label={`Base: ${step.details.search_results.search_info.knowledge_base}`}
                                      color="primary"
                                      sx={{ fontSize: '0.65rem', height: 16 }}
                                    />
                                    <Chip 
                                      size="small" 
                                      label={`Método: ${step.details.search_results.search_info.search_method}`}
                                      color="info"
                                      sx={{ fontSize: '0.65rem', height: 16 }}
                                    />
                                    <Chip 
                                      size="small" 
                                      label={`${step.details.search_results.search_info.search_time_ms?.toFixed(0)}ms`}
                                      color="secondary"
                                      sx={{ fontSize: '0.65rem', height: 16 }}
                                    />
                                    <Chip 
                                      size="small" 
                                      label={`${step.details.search_results.total_found} resultados`}
                                      color="success"
                                      sx={{ fontSize: '0.65rem', height: 16 }}
                                    />
                                    {step.details.search_results.search_info.top_k_used && (
                                      <Chip 
                                        size="small" 
                                        label={`top_k: ${step.details.search_results.search_info.top_k_used}`}
                                        color="info"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: 16 }}
                                      />
                                    )}
                                    {step.details.search_results.search_info.threshold_used && (
                                      <Chip 
                                        size="small" 
                                        label={`threshold: ${step.details.search_results.search_info.threshold_used}`}
                                        color="warning"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: 16 }}
                                      />
                                    )}
                                    {step.details.search_results.search_info.semantic_weight && (
                                      <Chip 
                                        size="small" 
                                        label={`sem: ${(step.details.search_results.search_info.semantic_weight * 100).toFixed(0)}%`}
                                        color="primary"
                                        variant="filled"
                                        sx={{ fontSize: '0.6rem', height: 16 }}
                                      />
                                    )}
                                    {step.details.search_results.search_info.bm25_weight && (
                                      <Chip 
                                        size="small" 
                                        label={`bm25: ${(step.details.search_results.search_info.bm25_weight * 100).toFixed(0)}%`}
                                        color="primary"
                                        variant="filled"
                                        sx={{ fontSize: '0.6rem', height: 16 }}
                                      />
                                    )}
                                  </Box>
                                )}
                                {step.details.search_results.summary && (
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>
                                    {step.details.search_results.summary}
                                  </Typography>
                                )}
                              </Box>
                              
                              {/* Chunks Específicos Encontrados */}
                              {step.details.search_results.results && step.details.search_results.results.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                    📄 Chunks Encontrados ({step.details.search_results.results.length}):
                                  </Typography>
                                  <Stack spacing={1}>
                                    {step.details.search_results.results.map((result, idx) => (
                                      <Paper
                                        key={idx}
                                        elevation={0}
                                        sx={{
                                          p: 1.5,
                                          border: 1,
                                          borderColor: 'divider',
                                          borderRadius: 1,
                                          bgcolor: 'grey.50',
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <Chip
                                              size="small"
                                              label={`Relevância: ${(result.relevance_score * 100).toFixed(1)}%`}
                                              color={result.relevance_score >= 0.8 ? 'success' : result.relevance_score >= 0.6 ? 'warning' : 'default'}
                                              sx={{ fontSize: '0.6rem', height: 16 }}
                                            />
                                            <Chip
                                              size="small"
                                              label={result.source?.document || 'Documento'}
                                              color="info"
                                              variant="outlined"
                                              sx={{ fontSize: '0.6rem', height: 16 }}
                                            />
                                          </Box>
                                        </Box>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontSize: '0.7rem', 
                                            color: 'text.secondary',
                                            overflow: 'hidden',
                                            display: '-webkit-box',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 3,
                                          }}
                                        >
                                          {result.content}
                                        </Typography>
                                      </Paper>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                            </Box>
                          )}
                          
                          {step.details.call_id && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Call ID: {step.details.call_id}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {step.type === 'assistant_message' && step.details.content && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Conteúdo da resposta:
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1,
                              mt: 0.5,
                              bgcolor: 'grey.50',
                              fontSize: '0.8rem',
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            {step.details.content}
                          </Paper>
                        </Box>
                      )}

                      {step.details.usage && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Token Usage:
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            <Typography variant="caption">
                              Input: {step.details.usage.input_tokens}
                            </Typography>
                            <Typography variant="caption">
                              Output: {step.details.usage.output_tokens}
                            </Typography>
                            <Typography variant="caption" fontWeight="600">
                              Total: {step.details.usage.total_tokens}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Collapse>
                  </Paper>
                </motion.div>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default TraceDrawer;